/**
 * 역할 관리 팝업과 동일한 permissions 스냅샷을 만들기 위한 병합·빌드 로직.
 * 역할 목록 권한 열은 팝업과 동일하게 `countPermissionCheckboxTrues`(체크된 체크박스 수)를 사용합니다.
 */

/** 트리 그룹(백엔드 boardGroupCd / 대시보드 탭과 대응) */
export const BOARD_TREE_GROUPS = [
  { groupCd: 'TASK_INFO', title: '과제(프로젝트) 정보', lead: '과제(프로젝트) 정보 탭의 폴더·게시판 트리와 동일합니다.' },
  { groupCd: 'COOPERATION', title: '협업 게시판 · 자료실', lead: '협업 게시판·자료실 탭의 폴더·게시판 트리와 동일합니다.' },
];

export function milestoneProjectPermKey(projectUid) {
  return `MILESTONE_P:${projectUid}`;
}

export function boardPermKey(groupCd, boardUid) {
  return `${groupCd}:${boardUid}`;
}

export function defaultBoardPerm() {
  return { view: false, create: false, update: false, delete: false, manage: false };
}

/**
 * 멤버 역할 코드(OWNER, PI, RESEARCHER, OBSERVER)별 게시판·마일스톤 슬롯 신규 기본값.
 */
export function defaultBoardPermForMemberRoleCd(roleCd) {
  const r = String(roleCd || '').toUpperCase();
  const full = { view: true, create: true, update: true, delete: true, manage: true };
  const contributor = { view: true, create: true, update: true, delete: true, manage: false };
  const reader = { view: true, create: false, update: false, delete: false, manage: false };
  if (r === 'OWNER') return { ...full };
  if (r === 'PI' || r === 'RESEARCHER') return { ...contributor };
  if (r === 'OBSERVER') return { ...reader };
  return { ...defaultBoardPerm() };
}

/** API 노드: BOARD 리프만 권한 부여 대상 */
export function isAllianceBoardLeaf(node) {
  if (!node) return false;
  const kind = String(node.boardKindCd || '').toUpperCase();
  if (kind === 'BOARD') return true;
  if (kind === 'FOLDER') return false;
  return !(Array.isArray(node.children) && node.children.length > 0);
}

export function collectBoardLeafKeys(groupCd, nodes, out = []) {
  (nodes || []).forEach((node) => {
    if (isAllianceBoardLeaf(node)) {
      const uid = node.boardUid ?? node.id;
      if (uid != null && uid !== '') out.push(boardPermKey(groupCd, uid));
    }
    if (node.children?.length) collectBoardLeafKeys(groupCd, node.children, out);
  });
  return out;
}

export function mergeBoardSlots(existingBoards, boardTrees, memberRoleCd) {
  const slotDefault = defaultBoardPermForMemberRoleCd(memberRoleCd);
  const next = { ...(existingBoards || {}) };
  BOARD_TREE_GROUPS.forEach(({ groupCd }) => {
    const nodes = boardTrees?.[groupCd] ?? [];
    collectBoardLeafKeys(groupCd, nodes).forEach((k) => {
      if (!(k in next)) next[k] = { ...slotDefault };
    });
  });
  return next;
}

export function mergeMilestoneSlots(existingBoards, milestoneProjects, memberRoleCd) {
  const slotDefault = defaultBoardPermForMemberRoleCd(memberRoleCd);
  const next = { ...(existingBoards || {}) };
  (milestoneProjects || []).forEach((project) => {
    const pUid = project?.id;
    if (pUid == null || pUid === '') return;
    const pk = milestoneProjectPermKey(pUid);
    if (!(pk in next)) next[pk] = { ...slotDefault };
  });
  return next;
}

export const CONSORTIUM_MEMBER_PERM_DEF = () => ({
  memberView: false,
  invite: false,
  roleChange: false,
  accessSuspend: false,
  memberRemove: false,
});

export function normalizeConsortiumPermissions(raw) {
  const d = CONSORTIUM_MEMBER_PERM_DEF();
  if (!raw || typeof raw !== 'object') return d;
  const isNewShape =
    'memberView' in raw
    || 'invite' in raw
    || 'roleChange' in raw
    || 'accessSuspend' in raw
    || 'memberRemove' in raw;
  if (isNewShape) {
    return { ...d, ...raw };
  }
  return {
    ...d,
    memberView: Boolean(raw.view),
    invite: Boolean(raw.create),
    roleChange: Boolean(raw.update),
    memberRemove: Boolean(raw.delete),
    accessSuspend: Boolean(raw.accessSuspend),
  };
}

export function emptyDynamicSlotsForRole(dynamicMenus, memberRoleCd) {
  const slotDefault = defaultBoardPermForMemberRoleCd(memberRoleCd);
  const out = {};
  (dynamicMenus || []).forEach((m) => {
    const id = String(m?.id ?? m?.menuId ?? m?.name ?? '');
    if (!id) return;
    out[id] = { ...slotDefault };
  });
  return out;
}

export function buildInitialPermissions(roleRow, initialPermissions, dynamicMenus, serverPermissions) {
  const memberRoleCd = roleRow?.id ?? roleRow?.roleKey;
  const base = {
    consortium: CONSORTIUM_MEMBER_PERM_DEF(),
    boards: {},
    calendar: { view: false, create: false, update: false, delete: false, manage: false },
    dynamic: emptyDynamicSlotsForRole(dynamicMenus, memberRoleCd),
  };
  const sections = ['consortium', 'calendar'];
  if (initialPermissions && typeof initialPermissions === 'object') {
    sections.forEach((s) => {
      if (initialPermissions[s] && typeof initialPermissions[s] === 'object') {
        base[s] =
          s === 'consortium'
            ? normalizeConsortiumPermissions({ ...base[s], ...initialPermissions[s] })
            : { ...base[s], ...initialPermissions[s] };
      }
    });
    if (initialPermissions.boards && typeof initialPermissions.boards === 'object') {
      base.boards = { ...base.boards, ...initialPermissions.boards };
    }
    if (initialPermissions.dynamic && typeof initialPermissions.dynamic === 'object') {
      base.dynamic = { ...base.dynamic, ...initialPermissions.dynamic };
    }
  }
  if (roleRow?.defaultPermissions && typeof roleRow.defaultPermissions === 'object') {
    sections.forEach((s) => {
      if (roleRow.defaultPermissions[s]) {
        base[s] =
          s === 'consortium'
            ? normalizeConsortiumPermissions({ ...base[s], ...roleRow.defaultPermissions[s] })
            : { ...base[s], ...roleRow.defaultPermissions[s] };
      }
    });
    if (roleRow.defaultPermissions.boards && typeof roleRow.defaultPermissions.boards === 'object') {
      base.boards = { ...base.boards, ...roleRow.defaultPermissions.boards };
    }
    if (roleRow.defaultPermissions.dynamic) {
      base.dynamic = { ...base.dynamic, ...roleRow.defaultPermissions.dynamic };
    }
  }
  if (serverPermissions && typeof serverPermissions === 'object') {
    sections.forEach((s) => {
      if (serverPermissions[s] && typeof serverPermissions[s] === 'object') {
        base[s] =
          s === 'consortium'
            ? normalizeConsortiumPermissions({ ...base[s], ...serverPermissions[s] })
            : { ...base[s], ...serverPermissions[s] };
      }
    });
    if (serverPermissions.boards && typeof serverPermissions.boards === 'object') {
      base.boards = { ...base.boards, ...serverPermissions.boards };
    }
    if (serverPermissions.dynamic && typeof serverPermissions.dynamic === 'object') {
      base.dynamic = { ...base.dynamic, ...serverPermissions.dynamic };
    }
  }
  return base;
}

/**
 * 팝업에서 trees 로딩 완료 후 적용하는 것과 동일한 최종 permissions 스냅샷.
 */
export function computeEffectivePermissionsSnapshot(
  roleRow,
  initialPermissions,
  dynamicMenus,
  serverPermissions,
  boardTrees,
  milestoneProjects,
) {
  const p = buildInitialPermissions(roleRow, initialPermissions, dynamicMenus, serverPermissions);
  const roleCd = roleRow?.id ?? roleRow?.roleKey;
  return {
    ...p,
    boards: mergeMilestoneSlots(mergeBoardSlots(p.boards || {}, boardTrees, roleCd), milestoneProjects, roleCd),
  };
}

/** 팝업에 실제로 그려지는 체크박스 필드만 (서버·병합 과정의 잉여 JSON 키는 제외) */
const CONSORTIUM_CHECKBOX_KEYS = ['memberView', 'invite', 'roleChange', 'accessSuspend', 'memberRemove'];
const CALENDAR_AND_SLOT_CHECKBOX_KEYS = ['view', 'create', 'update', 'delete', 'manage'];

function countTruesForKeys(obj, keys) {
  if (!obj || typeof obj !== 'object') return 0;
  let c = 0;
  keys.forEach((k) => {
    if (Boolean(obj[k])) c += 1;
  });
  return c;
}

/** 역할 관리 팝업과 동일: 화면에 있는 체크박스만 합산 */
export function countPermissionCheckboxTrues(perms) {
  if (!perms || typeof perms !== 'object') return 0;
  let n = countTruesForKeys(perms.consortium, CONSORTIUM_CHECKBOX_KEYS);
  n += countTruesForKeys(perms.calendar, CALENDAR_AND_SLOT_CHECKBOX_KEYS);
  const boards = perms.boards && typeof perms.boards === 'object' ? perms.boards : {};
  Object.values(boards).forEach((slot) => {
    n += countTruesForKeys(slot, CALENDAR_AND_SLOT_CHECKBOX_KEYS);
  });
  const dynamic = perms.dynamic && typeof perms.dynamic === 'object' ? perms.dynamic : {};
  Object.values(dynamic).forEach((slot) => {
    n += countTruesForKeys(slot, CALENDAR_AND_SLOT_CHECKBOX_KEYS);
  });
  return n;
}
